<div class="relative">
  <!-- Background Image -->
  <div class="absolute inset-0 z-0">
    <img
      src="assets/images/the_mikealicious_company_full.png"
      alt="Mikealicious Company Background"
      class="w-full h-full object-cover opacity-60"
    />
  </div>

  <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
    <!-- Hero Section -->
    <div class="text-center mb-16">
      <div class="mb-8">
        <img
          src="assets/images/mikealicious.JPG"
          alt="Michael Relling"
          class="w-48 h-48 rounded-full mx-auto object-cover shadow-lg"
        />
      </div>
      <div
        class="bg-white bg-opacity-90 p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 mb-16"
      >
        <h1 class="text-4xl font-bold text-gray-900 mb-4">
          Hey, I'm Michael! 👋
        </h1>
        <p class="text-xl text-gray-600 max-w-3xl mx-auto">
          Software engineer by day, code wizard by night, and passionate about
          crafting elegant solutions! ✨
        </p>
      </div>
    </div>

    <!-- Professional Background -->
    <div
      class="bg-white bg-opacity-90 p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 mb-16"
    >
      <h2 class="text-2xl font-bold text-gray-900 mb-6">What I Do</h2>
      <div class="space-y-6">
        <p class="text-gray-600">
          I craft custom software solutions that make businesses go "Wow!" and
          developers go "Ahh, that's clean code!" As a consultant, I turn
          complex problems into elegant solutions (and occasionally turn coffee
          into code).
        </p>
        <p class="text-gray-600">
          When I'm not making pixels dance with Angular and Tailwind, you might
          find me orchestrating cloud symphonies with Firebase, AWS, or even
          venturing into the mysterious realm of Azure (yes, I've survived to
          tell the tale! 🧙‍♂️).
        </p>
      </div>
    </div>

    <!-- Tech Stack -->
    <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
      <div
        class="bg-white bg-opacity-90 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
      >
        <div class="text-blue-600 mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-8 w-8"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h3 class="text-xl font-semibold mb-2">Frontend Maestro</h3>
        <p class="text-gray-600">
          Angular + Tailwind artistry, creating interfaces that are not just
          pretty faces, but have PhD-level functionality!
        </p>
      </div>

      <div
        class="bg-white bg-opacity-90 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
      >
        <div class="text-blue-600 mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-8 w-8"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
            />
          </svg>
        </div>
        <h3 class="text-xl font-semibold mb-2">Backend Sorcerer</h3>
        <p class="text-gray-600">
          Node.js, Express, and cloud solutions that would make a DevOps
          engineer proud (or at least nod approvingly).
        </p>
      </div>

      <div
        class="bg-white bg-opacity-90 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
      >
        <div class="text-blue-600 mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-8 w-8"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
        </div>
        <h3 class="text-xl font-semibold mb-2">Architecture Virtuoso</h3>
        <p class="text-gray-600">
          Building scalable systems with clean architecture principles. Making complex
          software feel like a well-orchestrated symphony! 🏗️
        </p>
      </div>
    </div>

    <!-- Let's Connect -->
    <div class="bg-gray-50 bg-opacity-90 rounded-xl p-8 text-center">
      <h2 class="text-3xl font-bold mb-8">Let's Create Something Amazing!</h2>
      <p class="text-gray-600 max-w-3xl mx-auto mb-8">
        Got an idea that needs bringing to life? Or maybe just want to geek out
        about the latest tech? I'm always up for an interesting project or a
        good tech conversation!
      </p>
      <div class="space-x-4">
        <a
          href="https://www.linkedin.com/in/michael-relling/"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-300"
        >
          Connect on LinkedIn
        </a>
        <a
          href="mailto:mike@mikealicious.co.za"
          class="inline-block bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors duration-300"
        >
          Email Me
        </a>
      </div>
    </div>
  </div>
</div>
